/**
 * A MEMBER'S ROLE IN A GROUP (ITP-E-204-07, Plan Q12). The chat panel's Members pane shows each member's role, and
 * lets a reader who may manage change it, by the members dialog's own rules (ShareItems offers only the roles at or
 * below the reader's own, and never a member above them):
 *
 *  - nobody changes their own role here;
 *  - a reader who cannot manage (a participant) changes nobody's, and nobody changes a member above them;
 *  - otherwise every role at or below the reader's own, other than the member's current one;
 *  - the group's only admin is never demoted: the server would refuse them leaving, and the group would have no admin.
 */

export const ADMIN = 290;
export const MANAGER = 280;
export const PARTICIPANT = 270;

/** The roles a member can hold, highest first. */
const ROLES = [ADMIN, MANAGER, PARTICIPANT] as const;
export type MemberRole = typeof ROLES[number];

const LABELS: Readonly<Record<MemberRole, string>> = {[ADMIN]: 'Admin', [MANAGER]: 'Manager', [PARTICIPANT]: 'Participant'};
const ACTIONS: Readonly<Record<MemberRole, string>> = {
  [ADMIN]: 'Make admin', [MANAGER]: 'Make manager', [PARTICIPANT]: 'Make participant'
};

export function roleLabel(permissionType: number): string {
  return LABELS[permissionType as MemberRole] ?? LABELS[PARTICIPANT];
}

/** The menu item that sets `role`. */
export function roleAction(role: MemberRole): string {
  return ACTIONS[role];
}

export interface RoleOptionsFacts {
  /** The reader: their email and the permission they manage with (`maxPermission`: 290, 280, or 0 for a participant). */
  me: { email: string | null | undefined; maxPermission: number };
  member: { email: string; permissionType: number };
  /** How many admins the group has now. */
  adminCount: number;
}

/** The roles the reader may give `member`, highest first; empty when the row offers no change. */
export function roleOptions({me, member, adminCount}: RoleOptionsFacts): MemberRole[] {
  const self = (me?.email || '').trim().toLowerCase();
  const level = me?.maxPermission || 0;
  if (!member || (self && (member.email || '').toLowerCase() === self) || level < MANAGER || member.permissionType > level) {
    return [];
  }
  if (isOnlyAdmin(member, adminCount)) {
    return [];
  }
  return ROLES.filter(role => role <= level && role !== member.permissionType);
}

/**
 * The roles the reader may give someone they add (ITP-E-204-07 Part B, Q13): at or below their own, lowest first, so
 * the everyday choice leads. Nothing for a reader who cannot manage.
 */
export function addRoleOptions(maxPermission: number): MemberRole[] {
  const level = maxPermission || 0;
  return level < MANAGER ? [] : [PARTICIPANT, MANAGER, ADMIN].filter(role => role <= level) as MemberRole[];
}

const ADD_ACTIONS: Readonly<Record<MemberRole, string>> = {
  [ADMIN]: 'Add as admin', [MANAGER]: 'Add as manager', [PARTICIPANT]: 'Add as participant'
};

/** The Add pane's menu item for `role`. */
export function addRoleAction(role: MemberRole): string {
  return ADD_ACTIONS[role];
}

export function isOnlyAdmin(member: { permissionType: number }, adminCount: number): boolean {
  return member?.permissionType === ADMIN && adminCount === 1;
}
